'use client';

import { useUserData } from '@/context/auth/user-data-context';
import type { Program } from '@/graphql/graphql';
import { fetchSubProgramsList } from '@/lib/api-programs';
import { uniqBy } from '@/lib/utils';
import { useCallback, useEffect, useRef, useState } from 'react';

export const EPISODE_PAGE_SIZE = 10;

type FetchEpisodesOptions = {
    skipAuth?: boolean;
    includeSensitiveData?: boolean;
    includeMasterFiles?: boolean;
};

const sellerFallbackOptions: FetchEpisodesOptions = {
    skipAuth: true,
    includeSensitiveData: false,
    includeMasterFiles: false,
};

interface UseEpisodePaginationProps {
    programId: string;
    includeMasterFiles?: boolean;
    skipAuth?: boolean;
    enabled?: boolean;
}

export const useEpisodePagination = ({
    programId,
    includeMasterFiles = false,
    skipAuth = false,
    enabled = true,
}: UseEpisodePaginationProps) => {
    const [episodes, setEpisodes] = useState<Program[]>([]);
    const [totalCount, setTotalCount] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [hasLoaded, setHasLoaded] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    const isPaginatingRef = useRef(false);
    const hasLoadedRef = useRef(false);
    const effectiveQueryOptionsRef = useRef<FetchEpisodesOptions>({
        skipAuth,
        includeSensitiveData: false,
        includeMasterFiles,
    });

    const loadingInitial = enabled && !hasLoaded;

    const { isSeller } = useUserData();

    const fetchEpisodes = useCallback(
        async (page: number, options?: FetchEpisodesOptions) => {
            const result = await fetchSubProgramsList({
                id: programId,
                page,
                pageSize: EPISODE_PAGE_SIZE,
                includeSensitiveData: options?.includeSensitiveData ?? false,
                includeMasterFiles: options?.includeMasterFiles ?? includeMasterFiles,
                skipAuth: options?.skipAuth ?? skipAuth,
            });

            result.data.sort((a: Program, b: Program) => (a.episodeNumber ?? 0) - (b.episodeNumber ?? 0));

            return result;
        },
        [programId, includeMasterFiles, skipAuth],
    );

    useEffect(() => {
        if (!programId || !enabled || hasLoadedRef.current) return;

        const loadInitial = async () => {
            try {
                let queryOptions = effectiveQueryOptionsRef.current;
                let result = await fetchEpisodes(1, queryOptions);

                if (result.total === 0 && isSeller) {
                    queryOptions = sellerFallbackOptions;
                    result = await fetchEpisodes(1, queryOptions);
                }

                effectiveQueryOptionsRef.current = queryOptions;
                setEpisodes(result.data);
                setTotalCount(result.total);
                setCurrentPage(1);
            } catch {
                return;
            } finally {
                hasLoadedRef.current = true;
                setHasLoaded(true);
            }
        };

        loadInitial();
    }, [programId, enabled, fetchEpisodes]);

    const loadMoreEpisodes = useCallback(async () => {
        if (isPaginatingRef.current || loadingMore) return;
        if (episodes.length >= totalCount) return;

        isPaginatingRef.current = true;
        setLoadingMore(true);
        try {
            const nextPage = currentPage + 1;
            const result = await fetchEpisodes(nextPage, effectiveQueryOptionsRef.current);
            setEpisodes((prev) => uniqBy([...prev, ...result.data], (p) => p.identifier.id));
            setCurrentPage(nextPage);
            setTotalCount(result.total);
        } catch {
            return;
        } finally {
            setLoadingMore(false);
            isPaginatingRef.current = false;
        }
    }, [loadingMore, episodes.length, totalCount, currentPage, fetchEpisodes]);

    useEffect(() => {
        hasLoadedRef.current = false;
        effectiveQueryOptionsRef.current = {
            skipAuth,
            includeSensitiveData: false,
            includeMasterFiles,
        };
        setHasLoaded(false);
        setEpisodes([]);
        setTotalCount(0);
        setCurrentPage(1);
    }, [programId, includeMasterFiles, skipAuth]);

    return {
        episodes,
        totalCount,
        loadingInitial,
        loadingMore,
        hasMore: episodes.length < totalCount,
        loadMoreEpisodes,
        setEpisodes,
    };
};
